/**
 * Builds the work-path template payload the API accepts.
 *
 * Separate from the dialog so the shape can be tested without a renderer —
 * which is what was missing when a template containing any new row started
 * failing with "Invalid uuid". Two differences between what the editor holds
 * and what the API takes are easy to miss and were both wrong:
 *
 *  - a row the user just added carries a client-side React key as `id`, and
 *    the API types `id` as a uuid, so sending it rejected the whole save;
 *  - the measurement unit is nested under `config`, so a flat `unit` was
 *    dropped by the schema without an error.
 */

import type { WorkPathTask, WorkPathTemplateInput } from '@/hooks/useWorkPaths';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A stored row keeps its uuid; a draft's React key is left out. */
function storedId(id: string | undefined): { id?: string } {
  return id !== undefined && UUID_PATTERN.test(id) ? { id } : {};
}

export interface TemplatePayloadInput {
  name: string;
  description: string;
  isDefault: boolean;
  selectedServiceIds: string[];
  tasks: WorkPathTask[];
  sortOrder: number;
}

export function buildTemplatePayload(input: TemplatePayloadInput): WorkPathTemplateInput {
  return {
    name: input.name.trim(),
    description: input.description.trim() || null,
    isDefault: input.isDefault,
    serviceIds: input.selectedServiceIds.length > 0 ? input.selectedServiceIds : null,
    sortOrder: input.sortOrder,
    tasks: input.tasks.map((task, taskIndex) => {
      const { id: _taskId, verifications, ...taskRest } = task;
      void _taskId;
      return {
        ...taskRest,
        ...storedId(task.id),
        // Position wins over whatever the row happened to carry: the editor
        // reorders in place, so an index is the only trustworthy source.
        sortOrder: taskIndex,
        verifications: verifications.map((verification, index) => {
          const { id: _id, unit, ...rest } = verification;
          void _id;
          return {
            ...rest,
            ...storedId(verification.id),
            sortOrder: index,
            ...(unit ? { config: { unit } } : {}),
          };
        }),
      };
    }),
  };
}
